import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, inject, isDevMode, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { LuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { API_BASE_URL } from '@core/api/api.tokens';
import { provideError } from '@core/errors/error.provider';
import { TranslocoHttpLoader } from '@core/transloco/transloco.http-loader';
import { provideToastComponent } from '@core/ui/components/toast';
import { provideErrorDialogUi } from '@core/ui/dialogs/error-dialog/error-dialog.provider';
import { CustomPaginatorIntlService } from '@core/ui/services/custom-paginator-intl.service';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

const LANGUAGE_STORAGE_KEY = 'mzeit.language';

function getInitialLanguage(fallback = 'de'): string {
    if (typeof localStorage === 'undefined') {
        return fallback;
    }

    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored === 'de' || stored === 'en' ? stored : fallback;
}

function getInitialLocale(): string {
    return getInitialLanguage() === 'en' ? 'en-US' : 'de-DE';
}
export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        provideError(),
        provideErrorDialogUi(),
        provideToastComponent({
            defaultDuration: 2500,
            defaultPosition: 'top-right',
            defaultVariant: 'fill',
        }),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
            useValue: {
                firstDayOfWeek: 1,
            },
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'dd.MM.yyyy',
                    timeInput: ['HH:mm', 'H:mm'],
                },
                display: {
                    timeInput: 'HH:mm',
                    timeOptionLabel: 'HH:mm',
                    dateInput: 'dd.MM.yyyy',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },
        { provide: LOCALE_ID, useFactory: () => getInitialLocale() },
        { provide: MAT_DATE_LOCALE, useFactory: () => getInitialLocale() },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'de',
                        label: 'Deutsch',
                    },
                    {
                        id: 'en',
                        label: 'English',
                    },
                ],
                defaultLang: 'de',
                fallbackLang: 'de',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        { provide: MatPaginatorIntl, useClass: CustomPaginatorIntlService },
        /*{
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },*/

        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const lang = getInitialLanguage(translocoService.getDefaultLang());
            translocoService.setActiveLang(lang);
            if (typeof document !== 'undefined') {
                document.documentElement.lang = lang;
            }

            return firstValueFrom(translocoService.load(lang));
        }),
        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classy',
                scheme: 'auto',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-amber',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};
